import { Download, FileCheck2, PackagePlus, RefreshCw } from 'lucide-react'

import { Button, StatusMessage } from '@krp/ui-kit'

import type { FileActions, FileVm } from '../model/types'
import { QueryState } from '../../pricing/ui/QueryState'
import '../../pricing/ui/onec-ui.css'
import { ReportView } from './ReportView'

/** Окно «Файл накладной»: сведения, разметка, отчёт проверки по rid. */
export function ImportFileScreen({ vm, actions }: { vm: FileVm; actions: FileActions }) {
  // Поставка из файла с блокирующими ошибками или без отчёта осталась бы пустым черновиком: служба отказала бы в заполнении.
  const supplyBlockedText =
    vm.report.query.status !== 'ready'
      ? 'Поставку из накладной можно создать, когда есть отчёт проверки файла'
      : vm.report.isBlocked
        ? 'В отчёте есть блокирующие ошибки — поставку из этого файла не заполнить'
        : null
  return (
    <section className="onec-screen" aria-labelledby="onec-file-title">
      <header className="onec-section__head">
        <h2 id="onec-file-title">Файл накладной</h2>
        <div className="onec-row">
          <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload}>
            Обновить
          </Button>
          <Button variant="ghost" onClick={actions.close}>
            Закрыть
          </Button>
        </div>
      </header>

      <QueryState query={vm.file.query} emptyText="Файл не найден" onRetry={actions.reload} />
      {vm.file.query.status === 'ready' && (
        <section className="onec-section" aria-labelledby="onec-file-info">
          <h3 id="onec-file-info">Сведения</h3>
          <dl>
            {vm.file.info.map((item) => (
              <div key={item.label}>
                <dt className="onec-muted">{item.label}</dt>
                <dd>{item.value}</dd>
              </div>
            ))}
          </dl>
          {vm.file.matchedByText !== null && <p>{vm.file.matchedByText}</p>}
          <div className="onec-row">
            {/* Остальные отказы (нет права import.read, файл исчез) служба скажет словами в окне поставки. */}
            <Button
              variant="primary"
              icon={<PackagePlus size={16} aria-hidden="true" />}
              onClick={actions.createSupply}
              disabled={supplyBlockedText !== null}
            >
              Создать поставку из накладной
            </Button>
            {supplyBlockedText !== null && <p className="onec-muted">{supplyBlockedText}</p>}
          </div>
          {vm.file.mapping.length > 0 && (
            <div className="onec-scroll">
              <table className="onec-table">
                <caption className="onec-muted">Разметка колонок</caption>
                <thead>
                  <tr>
                    <th>Колонка</th>
                    <th>Заголовок</th>
                    <th>Роль</th>
                  </tr>
                </thead>
                <tbody>
                  {vm.file.mapping.map((row) => (
                    <tr key={row.column}>
                      <td>{row.column}</td>
                      <td>{row.header || '—'}</td>
                      <td>{row.role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <section className="onec-section" aria-labelledby="onec-file-report">
        <div className="onec-section__head">
          <h3 id="onec-file-report">Отчёт проверки</h3>
          <Button icon={<FileCheck2 size={16} aria-hidden="true" />} busy={vm.validation.busy} disabled={vm.configurationVersion === null} onClick={actions.validate}>
            {vm.validation.unknownOutcome ? 'Повторить проверку' : 'Проверить заново'}
          </Button>
        </div>
        {vm.validation.unknownOutcome && (
          <StatusMessage kind="unavailable" title="Неизвестно, выполнена ли проверка" text="Ответ не получен. Повтор безопасен." />
        )}
        {vm.validation.error !== null && !vm.validation.unknownOutcome && (
          <StatusMessage
            kind="error"
            title="Проверка не выполнена"
            text={vm.validation.error.message}
            meta={`Код: ${vm.validation.error.code}${vm.validation.error.correlationId === null ? '' : ' · запрос ' + vm.validation.error.correlationId}`}
          />
        )}
        <ReportView report={vm.report} emptyText="Отчёта ещё нет — проверьте накладную" onRetry={actions.reload} />
      </section>

      <section className="onec-section" aria-labelledby="onec-file-download">
        <h3 id="onec-file-download">Скачать</h3>
        <div className="onec-row">
          <Button icon={<Download size={16} aria-hidden="true" />} onClick={actions.downloadOriginal}>
            Исходный файл
          </Button>
          <Button icon={<Download size={16} aria-hidden="true" />} onClick={actions.downloadReportCsv}>
            Отчёт (CSV)
          </Button>
        </div>
        {vm.download.errorText !== null && <StatusMessage kind="error" title="Файл не скачан" text={vm.download.errorText} />}
      </section>
    </section>
  )
}
